/**
 * thumbnailUtils — client-side thumbnail generation for document uploads.
 *
 * generateThumbnail(file) → Promise<Blob|null>
 *   - images (png/jpeg/webp): objectURL → <img> → canvas, max 320px
 *   - PDFs: pdf.js (lazy chunk, shared with PdfViewer) renders page 1
 *   - everything else / any failure → null
 *
 * Output is always JPEG (quality 0.82, white background) because Safari's
 * canvas cannot encode webp. Thumbnails are best-effort by contract: every
 * failure path resolves to null and must NEVER fail or slow-fail an upload.
 * HEIC/HEIF are deliberately excluded — Chrome can't decode them at all.
 */

import { getExtension } from '../../../constants/documentConstants';

const MAX_DIMENSION = 320;
const JPEG_QUALITY = 0.82;
const GENERATION_TIMEOUT_MS = 15000;

const IMAGE_THUMB_MIMES = ['image/png', 'image/jpeg', 'image/webp'];
const IMAGE_THUMB_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp'];

/** Should the upload pipeline request a thumbnail slot for this file? */
export function canGenerateThumbnail(file) {
  if (!file || !file.name) return false;
  const mime = String(file.type || '').toLowerCase();
  const ext = getExtension(file.name);
  if (mime === 'application/pdf' || ext === 'pdf') return true;
  return IMAGE_THUMB_MIMES.includes(mime) || IMAGE_THUMB_EXTENSIONS.includes(ext);
}

/** Scale (never upscale raster images) so the longest edge is MAX_DIMENSION. */
function fitDimensions(width, height, { allowUpscale = false } = {}) {
  const longest = Math.max(width, height);
  if (!longest) return null;
  let scale = MAX_DIMENSION / longest;
  if (!allowUpscale) scale = Math.min(1, scale);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
    scale,
  };
}

/** Draw a source onto a white canvas and encode as JPEG. */
function canvasToJpegBlob(canvas) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== 'function') {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => resolve(blob || null), 'image/jpeg', JPEG_QUALITY);
  });
}

function makeCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  // White background: JPEG has no alpha, so transparent PNG areas would
  // otherwise render black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  return { canvas, ctx };
}

async function generateImageThumbnail(file) {
  const objectUrl = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Image decode failed'));
      el.src = objectUrl;
    });
    const dims = fitDimensions(img.naturalWidth, img.naturalHeight);
    if (!dims) return null;
    const { canvas, ctx } = makeCanvas(dims.width, dims.height);
    ctx.drawImage(img, 0, 0, dims.width, dims.height);
    return await canvasToJpegBlob(canvas);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function generatePdfThumbnail(file) {
  // Lazy: pulls the same chunk the PdfViewer uses, so opening the viewer or
  // uploading a PDF costs the download once, and non-PDF users never pay it.
  const { pdfjs } = await import('./pdfWorker');
  const buffer = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data: buffer });
  try {
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);
    const baseViewport = page.getViewport({ scale: 1 });
    // PDFs are vector — upscaling tiny pages is fine and keeps thumbs crisp.
    const dims = fitDimensions(baseViewport.width, baseViewport.height, { allowUpscale: true });
    if (!dims) return null;
    const viewport = page.getViewport({ scale: dims.scale });
    const { canvas, ctx } = makeCanvas(Math.round(viewport.width), Math.round(viewport.height));
    await page.render({ canvasContext: ctx, viewport }).promise;
    return await canvasToJpegBlob(canvas);
  } finally {
    try {
      await loadingTask.destroy();
    } catch (_) {
      // best-effort cleanup
    }
  }
}

/**
 * Best-effort 320px JPEG preview. Resolves null on any failure or timeout —
 * callers treat null as "no thumbnail" and continue the upload normally.
 */
export async function generateThumbnail(file) {
  if (!canGenerateThumbnail(file)) return null;
  const mime = String(file.type || '').toLowerCase();
  const ext = getExtension(file.name);
  const isPdf = mime === 'application/pdf' || ext === 'pdf';

  const timeout = new Promise((resolve) => {
    setTimeout(() => resolve(null), GENERATION_TIMEOUT_MS);
  });
  const work = (isPdf ? generatePdfThumbnail(file) : generateImageThumbnail(file)).catch((err) => {
    console.warn(`Thumbnail generation failed (${file.name}):`, err?.message || err);
    return null;
  });
  return Promise.race([work, timeout]);
}

export default generateThumbnail;
